import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { Settings as SettingsIcon, RefreshCw, Save, Table2, BookOpen, Plus, Trash2, Tags, X } from 'lucide-react';
import toast from 'react-hot-toast';
import CageBalancingTabs from '../components/CageBalancingTabs';
import { useAuth } from '../contexts/AuthContext';

// Cage-activity types a custom JE line can pull its amount from (mirrors the
// Balance Sheet's activity categories).
const ACTIVITY_TYPES = [
  { v: 'cash_add', l: 'Cash Add' },
  { v: 'cash_removed', l: 'Cash Removed' },
  { v: 'chip_add', l: 'Chip Add' },
  { v: 'chips_removed', l: 'Chips Removed' },
  { v: 'fabicash', l: 'FABICash' },
  { v: 'atm_fill', l: 'ATM Fill' },
  { v: 'dealer_tray_variance', l: 'Dealer Tray Variance' },
  { v: 'podium_variance', l: 'Podium Variance' },
  { v: 'floor_promo', l: 'Floor Promo' },
  { v: 'morning_bonus', l: 'Morning Bonus' },
  { v: 'free_play_redeemed', l: 'Free Play Redeemed' },
  { v: 'poker_jackpot_payout', l: 'Poker Jackpot Payout' },
  { v: 'tcp_jackpot_payout', l: 'TCP Jackpot Payout' },
  { v: 'uth_jackpot_payout', l: 'UTH Jackpot Payout' },
  { v: 'bj_jackpot_payout', l: 'BJ Jackpot Payout' },
  { v: 'chip_buy', l: 'Chip Buy' },
  { v: 'player_bank', l: 'Player Bank' },
  { v: 'tournament_misc', l: 'Tournament / Misc' },
];
const POSTING_RULES = [
  { v: 'sign_split', l: 'Auto (debit if −, credit if +)' },
  { v: 'debit_always', l: 'Always debit' },
  { v: 'credit_always', l: 'Always credit' },
];

// ----------------------------------------------------------------
// Cage GL Mapping (settings)
// ----------------------------------------------------------------
// Self-serve mapping for the QuickBooks JE export, gated by cbs:configure:
//   1. JE account -> QuickBooks GL account (searchable dropdown from the
//      chart of accounts, with a free-text override).
//   2. Physical table -> JE account routing (re-point tables when the floor
//      layout changes, no code deploy needed).
// Backed by /admin/cbs/{gl-accounts, je-map, je-accounts/:key, table-routing}.

export default function CageGlMapping() {
  const { can } = useAuth();
  const allowed = can('cbs:configure');

  const [glAccounts, setGlAccounts] = useState([]);
  const [jeAccounts, setJeAccounts] = useState([]);
  const [routing, setRouting] = useState([]);
  const [qbEdits, setQbEdits] = useState({});     // account_key -> qb string
  const [routeEdits, setRouteEdits] = useState({}); // table_number -> account_key
  const [loading, setLoading] = useState(true);
  const [savingMap, setSavingMap] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [newLine, setNewLine] = useState({ accountName: '', qbAccountName: '', activityTypeKey: '', postingRule: 'sign_split' });
  const [savingCustom, setSavingCustom] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [actTypes, setActTypes] = useState([]);
  const [newActType, setNewActType] = useState('');
  const [savingActType, setSavingActType] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [glRes, jeRes, rtRes] = await Promise.all([
        api.get('/admin/cbs/gl-accounts'),
        api.get('/admin/cbs/je-map'),
        api.get('/admin/cbs/table-routing'),
      ]);
      setGlAccounts(glRes.data.glAccounts || []);
      setJeAccounts(jeRes.data.accounts || []);
      setRouting(rtRes.data.routing || []);
      // Custom activity types are optional — if that endpoint isn't live yet
      // (e.g. backend not deployed), don't let it break the rest of the page.
      try {
        const actRes = await api.get('/admin/cbs/activity-types');
        setActTypes(actRes.data.activityTypes || []);
      } catch {
        setActTypes([]);
      }
      setQbEdits({});
      setRouteEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load GL mapping.');
    } finally {
      setLoading(false);
    }
  }, []);

  const importChart = async () => {
    const accounts = importText.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!accounts.length) return;
    setImporting(true);
    try {
      const res = await api.post('/admin/cbs/gl-accounts/import', { accounts });
      toast.success(`Imported ${res.data.imported} accounts.`);
      setImportText('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  // account_key -> account_name, for the routing dropdown labels
  const jeByKey = useMemo(() => {
    const m = {};
    for (const a of jeAccounts) m[a.accountKey] = a;
    return m;
  }, [jeAccounts]);

  const allActivityTypes = useMemo(
    () => [...ACTIVITY_TYPES, ...actTypes.map((t) => ({ v: t.activityKey, l: t.label }))],
    [actTypes]
  );

  const qbValue = (a) => (qbEdits[a.accountKey] !== undefined ? qbEdits[a.accountKey] : a.qbAccountName || '');
  const routeValue = (r) => (routeEdits[r.tableNumber] !== undefined ? routeEdits[r.tableNumber] : r.accountKey);

  const mapChanged = Object.keys(qbEdits).length > 0;
  const routeChanged = Object.keys(routeEdits).length > 0;

  const saveMap = async () => {
    const entries = Object.entries(qbEdits);
    if (!entries.length) return;
    setSavingMap(true);
    try {
      for (const [key, qb] of entries) {
        await api.put(`/admin/cbs/je-accounts/${key}`, { qbAccountName: qb });
      }
      toast.success(`Saved ${entries.length} account mapping${entries.length > 1 ? 's' : ''}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save mapping.');
    } finally {
      setSavingMap(false);
    }
  };

  const saveRouting = async () => {
    const rows = Object.entries(routeEdits).map(([tableNumber, accountKey]) => ({
      tableNumber: Number(tableNumber),
      accountKey,
      label: jeByKey[accountKey]?.accountName || null,
    }));
    if (!rows.length) return;
    setSavingRoute(true);
    try {
      await api.put('/admin/cbs/table-routing', { routing: rows });
      toast.success(`Saved routing for ${rows.length} table${rows.length > 1 ? 's' : ''}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save routing.');
    } finally {
      setSavingRoute(false);
    }
  };

  const customLines = jeAccounts.filter((a) => a.isCustom);
  const addCustom = async () => {
    if (!newLine.accountName.trim() || !newLine.activityTypeKey) {
      return toast.error('Enter a name and choose a cage-activity type.');
    }
    setSavingCustom(true);
    try {
      await api.post('/admin/cbs/je-accounts', newLine);
      toast.success('Custom JE line added.');
      setNewLine({ accountName: '', qbAccountName: '', activityTypeKey: '', postingRule: 'sign_split' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add custom line.');
    } finally {
      setSavingCustom(false);
    }
  };
  const deleteCustom = async (key) => {
    try {
      await api.delete(`/admin/cbs/je-accounts/${key}`);
      toast.success('Custom JE line removed.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to remove line.');
    }
  };

  const addActType = async () => {
    const label = newActType.trim();
    if (!label) return;
    setSavingActType(true);
    try {
      await api.post('/admin/cbs/activity-types', { label });
      toast.success(`Added activity type "${label}".`);
      setNewActType('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add activity type.');
    } finally {
      setSavingActType(false);
    }
  };
  const deleteActType = async (key) => {
    try {
      await api.delete(`/admin/cbs/activity-types/${key}`);
      toast.success('Activity type removed.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to remove activity type.');
    }
  };

  if (!allowed) {
    return (
      <div>
        <CageBalancingTabs />
        <div className="rounded-lg border border-gray-700 bg-gray-900 p-6 text-gray-400">
          You don't have permission to configure the GL mapping (requires <code>cbs:configure</code>).
        </div>
      </div>
    );
  }

  const tableRevenueKeys = jeAccounts
    .filter((a) => a.postingRule === 'credit_always' && /Table #/.test(a.accountName))
    .map((a) => a.accountKey);

  return (
    <div>
      <CageBalancingTabs />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <SettingsIcon className="text-amber-400" /> GL Mapping
          </h1>
          <p className="text-sm text-gray-400">
            Map each journal-entry line to its QuickBooks account, and route each table's drop to the
            right revenue account. Used by the Balance Sheet's QuickBooks (IIF) export.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 rounded-lg border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-8">
          {/* Section 1: JE account -> QuickBooks account */}
          <section className="rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
                <BookOpen size={18} className="text-amber-400" /> JE Account → QuickBooks Account
              </h2>
              <button
                onClick={saveMap}
                disabled={!mapChanged || savingMap}
                className="flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-black disabled:opacity-40"
              >
                <Save size={16} /> {savingMap ? 'Saving…' : 'Save mapping'}
              </button>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              Start typing to search your chart of accounts, or paste an exact account path. Leave
              blank to fall back to the JE line's own name on export.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="py-2 pr-4 font-medium">JE Line</th>
                    <th className="py-2 pr-4 font-medium">QuickBooks Account</th>
                  </tr>
                </thead>
                <tbody>
                  {jeAccounts.map((a) => {
                    const changed = qbEdits[a.accountKey] !== undefined;
                    return (
                      <tr key={a.accountKey} className="border-t border-gray-800">
                        <td className="py-1.5 pr-4 text-gray-200 whitespace-nowrap">
                          {a.accountName}
                          <span className="ml-2 text-xs text-gray-600">{a.accountKey}</span>
                        </td>
                        <td className="py-1.5 pr-4">
                          <input
                            list="cbs-gl-options"
                            value={qbValue(a)}
                            onChange={(e) => setQbEdits((p) => ({ ...p, [a.accountKey]: e.target.value }))}
                            placeholder="— unmapped —"
                            className={`w-full min-w-[24rem] rounded border bg-gray-950 px-2 py-1 text-gray-100 ${
                              changed ? 'border-amber-500' : 'border-gray-700'
                            }`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 2: table routing */}
          <section className="rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
                <Table2 size={18} className="text-amber-400" /> Table → Account Routing
              </h2>
              <button
                onClick={saveRouting}
                disabled={!routeChanged || savingRoute}
                className="flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-black disabled:opacity-40"
              >
                <Save size={16} /> {savingRoute ? 'Saving…' : 'Save routing'}
              </button>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              When a table changes games, re-point its drop here — no code change needed. Each table's
              box drop posts to the selected JE revenue line.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="py-2 pr-4 font-medium">Physical Table</th>
                    <th className="py-2 pr-4 font-medium">Posts To (JE revenue line)</th>
                  </tr>
                </thead>
                <tbody>
                  {routing.map((r) => {
                    const changed = routeEdits[r.tableNumber] !== undefined;
                    return (
                      <tr key={r.tableNumber} className="border-t border-gray-800">
                        <td className="py-1.5 pr-4 text-gray-200 whitespace-nowrap">
                          Table #{r.tableNumber}
                          {r.label ? <span className="ml-2 text-xs text-gray-500">{r.label}</span> : null}
                        </td>
                        <td className="py-1.5 pr-4">
                          <select
                            value={routeValue(r)}
                            onChange={(e) => setRouteEdits((p) => ({ ...p, [r.tableNumber]: e.target.value }))}
                            className={`w-full min-w-[20rem] rounded border bg-gray-950 px-2 py-1 text-gray-100 ${
                              changed ? 'border-amber-500' : 'border-gray-700'
                            }`}
                          >
                            {(tableRevenueKeys.length ? tableRevenueKeys : jeAccounts.map((a) => a.accountKey)).map(
                              (k) => (
                                <option key={k} value={k}>
                                  {jeByKey[k]?.accountName || k}
                                </option>
                              )
                            )}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 3: custom JE lines */}
          <section className="rounded-lg border border-gray-700 bg-gray-900 p-4">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-white">
              <Plus size={18} className="text-amber-400" /> Custom JE Lines
            </h2>
            <p className="mb-3 text-xs text-gray-500">
              Add your own GL lines to the export. Each pulls its amount from a cage-activity type and
              appears on every day's journal (including at $0.00). Map it to a QuickBooks account in the
              grid above. Tip: choose an activity type that isn't already on a built-in line, or its
              amount will also flow into Daily Over/Short.
            </p>
            <div className="mb-4 rounded-lg border border-gray-800 bg-gray-950 p-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                <Tags size={14} className="text-amber-400" /> Activity types
              </div>
              <p className="mb-2 text-xs text-gray-500">
                Built-in types always appear. Add your own (e.g. Player Credit) to use them here and on
                the Balance Sheet's cage-activity entry.
              </p>
              {actTypes.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {actTypes.map((tp) => (
                    <span
                      key={tp.activityKey}
                      className="flex items-center gap-1.5 rounded-full border border-gray-700 bg-gray-900 px-2.5 py-1 text-xs text-gray-200"
                    >
                      {tp.label}
                      <button
                        onClick={() => deleteActType(tp.activityKey)}
                        className="text-gray-500 hover:text-red-400"
                        title="Remove type"
                      >
                        <X size={13} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={newActType}
                  onChange={(e) => setNewActType(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addActType(); }}
                  placeholder="New activity type (e.g. Player Credit)"
                  className="w-64 rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100"
                />
                <button
                  onClick={addActType}
                  disabled={savingActType || !newActType.trim()}
                  className="flex items-center gap-1.5 rounded-lg border border-amber-500/60 px-3 py-1.5 text-sm font-medium text-amber-300 hover:bg-amber-500/10 disabled:opacity-40"
                >
                  <Plus size={15} /> {savingActType ? 'Adding…' : 'Add type'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-6">
              <input
                placeholder="Account name"
                value={newLine.accountName}
                onChange={(e) => setNewLine((p) => ({ ...p, accountName: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100 md:col-span-2"
              />
              <input
                list="cbs-gl-options"
                placeholder="QuickBooks account (optional)"
                value={newLine.qbAccountName}
                onChange={(e) => setNewLine((p) => ({ ...p, qbAccountName: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100 md:col-span-2"
              />
              <select
                value={newLine.activityTypeKey}
                onChange={(e) => setNewLine((p) => ({ ...p, activityTypeKey: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100"
              >
                <option value="">— activity type —</option>
                {allActivityTypes.map((a) => (
                  <option key={a.v} value={a.v}>{a.l}</option>
                ))}
              </select>
              <select
                value={newLine.postingRule}
                onChange={(e) => setNewLine((p) => ({ ...p, postingRule: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100"
              >
                {POSTING_RULES.map((r) => (
                  <option key={r.v} value={r.v}>{r.l}</option>
                ))}
              </select>
            </div>
            <div className="mt-2">
              <button
                onClick={addCustom}
                disabled={savingCustom}
                className="flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium text-black disabled:opacity-40"
              >
                <Plus size={16} /> {savingCustom ? 'Adding…' : 'Add JE line'}
              </button>
            </div>
            {customLines.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400">
                      <th className="py-2 pr-4 font-medium">JE Line</th>
                      <th className="py-2 pr-4 font-medium">Activity source</th>
                      <th className="py-2 pr-4 font-medium">Posts</th>
                      <th className="py-2 pr-4 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {customLines.map((a) => {
                      const act = allActivityTypes.find((t) => t.v === a.activityTypeKey);
                      const rule = POSTING_RULES.find((r) => r.v === a.postingRule);
                      return (
                        <tr key={a.accountKey} className="border-t border-gray-800">
                          <td className="py-1.5 pr-4 text-gray-200">
                            {a.accountName}
                            {a.qbAccountName ? (
                              <span className="ml-2 text-xs text-gray-500">→ {a.qbAccountName}</span>
                            ) : null}
                          </td>
                          <td className="py-1.5 pr-4 text-gray-300">{act ? act.l : a.activityTypeKey}</td>
                          <td className="py-1.5 pr-4 text-gray-400">{rule ? rule.l : a.postingRule}</td>
                          <td className="py-1.5 pr-4 text-right">
                            <button
                              onClick={() => deleteCustom(a.accountKey)}
                              className="text-gray-500 hover:text-red-400"
                              title="Remove line"
                            >
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Section 4: import chart of accounts */}
          <section className="rounded-xl border border-gray-700 bg-gray-800 p-5">
            <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-white">
              <BookOpen size={18} className="text-amber-400" /> Chart of accounts
            </h2>
            <p className="mb-3 text-sm text-gray-400">
              The account list your GL mappings pull from ({glAccounts.length} loaded). Paste your
              QuickBooks chart of accounts (one full account path per line) to replace it. Your
              JE→account mappings are matched by name and kept.
            </p>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={'1000 - Revenue:1100 - Gaming Revenue:1120 - Poker:1121 - Texas Hold Em:Table #1\nJackpots:BJ Jackpot\n...'}
              rows={6}
              className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-amber-500"
            />
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                onClick={importChart}
                disabled={importing || !importText.trim()}
                className="flex items-center gap-2 rounded-lg bg-gray-700 px-3 py-2 text-sm font-medium text-white hover:bg-gray-600 disabled:opacity-40"
              >
                <RefreshCw size={16} /> {importing ? 'Importing…' : 'Replace chart of accounts'}
              </button>
              <span className="text-xs text-amber-400">Replaces the whole selectable list.</span>
            </div>
          </section>
        </div>
      )}

      {/* Shared datalist for the GL account search */}
      <datalist id="cbs-gl-options">
        {glAccounts.map((g) => (
          <option key={g.id} value={g.accountPath}>
            {g.accountType}
          </option>
        ))}
      </datalist>
    </div>
  );
}
